import React from 'react';
import { Assert } from '@cotera/utilities';
import {
  ModelScope,
  useAction,
  useInScopeModel,
  useWatchableValue,
} from '../actions/framework';
import { NotFoundTab } from '../not-found.tab';
import { DocumentModel } from './document.model';
import { GraphModel } from '../graph/graph.model';
import { useTabParams } from '../tab-manager/tab-manager';
import { Icon } from '@cotera/client/app/components/ui';
import { RichTextEditor } from '@cotera/client/app/components/app/rte';
import { SaveDocumentAction } from './save-document.action';
import { Button } from '../components/ui/button';
import {
  CoteraMention,
  MentionDropdownMenu,
} from '@cotera/client/app/components/ui/tiptap/extensions';
import { cn } from '../lib/utils';
import { useGraphMentions } from '@cotera/client/app/hooks/use-graph-metions';
import { useTrackRecentActivity } from '../hooks/use-track-recent-activity';
import { AppTab } from '../components/tab/tab';
import {
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenu,
} from '../components/ui/dropdown-menu';
import { DeleteDocumentAction } from './delete-document.action';
import { DuplicateAction } from '../graph/duplicate.action';

const useModel = () => {
  const { id } = useTabParams<{ id: string }>();
  const model = useInScopeModel(DocumentModel, id);

  Assert.assert(model !== null, 'Document Model not found');
  return model;
};

export const NewDocumentTab: React.FC = () => {
  const { id } = useTabParams<{ id: string }>();
  const graphModel = useInScopeModel(GraphModel);

  Assert.assert(graphModel !== null);

  const model = new DocumentModel(
    {
      name: 'New Document',
      graph: graphModel.graph,
      compiler: graphModel.compiler,
    },
    {
      id,
      isNew: true,
    }
  );

  return (
    <AppTab model={model}>
      <ModelScope models={[model]}>
        <DocumentPage />
      </ModelScope>
    </AppTab>
  );
};

export const DocumentTab: React.FC = () => {
  const { id: name } = useTabParams<{ tabId: string; id: string }>();
  const graphModel = useInScopeModel(GraphModel);

  Assert.assert(graphModel !== null);

  const graph = useWatchableValue(graphModel.graph);

  // Track viewing this document
  useTrackRecentActivity({ type: 'document', name });

  if (!graph.hasSymbol(name)) {
    return (
      <NotFoundTab title="Document Not Found" resource={`Document "${name}"`} />
    );
  }

  const model = new DocumentModel(
    {
      name,
      graph: graphModel.graph,
      compiler: graphModel.compiler,
    },
    {
      id: name,
    }
  );

  return (
    <AppTab model={model}>
      <ModelScope models={[model]}>
        <DocumentPage />
      </ModelScope>
    </AppTab>
  );
};

const DocumentPage: React.FC = () => {
  const model = useModel();
  Assert.assert(model !== null, 'Model not found');

  return (
    <div className="flex gap-4 h-full w-full py-4">
      <div className="flex flex-col gap-4 w-full h-full relative">
        <Header />
        <Editor />
      </div>
    </div>
  );
};

const Header: React.FC = () => {
  const saveAction = useAction(SaveDocumentAction);
  const duplicateDocument = useAction(DuplicateAction);
  const deleteDocument = useAction(DeleteDocumentAction);

  return (
    <div className="w-full sticky top-0 bg-background z-10">
      <div className="flex justify-end px-4 pt-2.5 pb-2 items-center">
        <Button variant={'outline'} onClick={() => saveAction.execute()}>
          <Icon icon="save" />
          Save
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size={'icon-sm'}>
              <Icon icon="ellipsis-vertical" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="bottom">
            <DropdownMenuItem onSelect={() => duplicateDocument.execute()}>
              <Icon icon="copy" />
              Duplicate Document
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => deleteDocument.execute()}>
              <Icon icon="trash" />
              Delete Document
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

const Editor: React.FC = () => {
  const model = useModel();
  const { documentState } = useWatchableValue(model.formState);

  const { options: mentionOptions, groups: mentionGroups } = useGraphMentions({
    useModel,
    types: ['document', 'tool'],
  });

  return (
    <div
      className={cn(
        'transition-all duration-500 ease-in-out pt-10 w-full md:max-w-5xl mx-auto relative',
        'shadow-none flex-1 min-h-0 flex flex-col'
      )}
    >
      <div className="flex-1 min-h-0">
        <RichTextEditor
          className="h-full overflow-y-auto"
          titlePlaceholder="Give your document a title..."
          placeholder="Start writing your document..."
          content={documentState}
          extensions={[
            {
              extension: CoteraMention,
              config: {
                // No menus - mentions are triggered by @ character only
              },
            },
          ]}
          onUpdate={(editor) => {
            model.formState.set({
              ...model.formState.snapshot(),
              documentState: editor.getJSON(),
            });
          }}
        >
          <MentionDropdownMenu
            options={mentionOptions}
            groups={mentionGroups}
          />
        </RichTextEditor>
      </div>
    </div>
  );
};
